import { useState } from 'react';
import { Combobox, InlineStack, Select, SelectMenuOptionContent } from '@customerio/pluma-components/react';
import { fieldError } from '@customerio/dryad';
import type { BuilderError } from '@customerio/dryad';
import { isEmptyString } from '@/utils/string';
import {
  clearsValues,
  isTwoValueFilterOperator,
  isUnaryFilterOperator,
  type AttributeFilterState,
  type OperatorGroup,
} from './attribute-filter';

type OperatorValuePatch = Partial<Pick<AttributeFilterState, 'operator' | 'value' | 'value2'>>;

type ValueOption = { value: string; label: string };

type MaskedValueOptionProps = {
  option: ValueOption;
  isSelected?: boolean;
  isActive?: boolean;
};

/**
 * The autosuggest options are the workspace's real stored attribute values —
 * subscriber PII for fields like email or phone. The combobox popover portals
 * to document.body, outside the input's own fs-mask, so each option masks
 * itself to keep the values out of FullStory session replay (FPS-3714).
 */
function MaskedValueOption(props: MaskedValueOptionProps) {
  return <SelectMenuOptionContent {...props} className="fs-mask" />;
}

type OperatorValueProps = {
  operator: string;
  value: string | null;
  valueOptions: ValueOption[];
  isLoadingValueOptions?: boolean;
  operatorGroups: OperatorGroup[];
  disabled?: boolean;
  errors: BuilderError[];
  onChange: (patch: OperatorValuePatch) => void;
};

/**
 * The operator select plus the value editor its choice needs. This surface
 * declares only the equality + existence families, so the reachable editors
 * are the operator select and the single-value combobox — unary operators
 * (exists, does not exist) render no value input at all.
 */
export function OperatorValue({
  operator,
  value,
  valueOptions,
  isLoadingValueOptions,
  operatorGroups,
  disabled,
  errors,
  onChange,
}: OperatorValueProps) {
  // "Enter a value" renders only once the input has been visited — or from
  // the start when the row mounted holding a value, whose problems block the
  // commit either way.
  const [valueTouched, setValueTouched] = useState(() => !isEmptyString(value));

  // Always shown, untouched or not: an operator-tagged mismatch makes the
  // tree invalid — which stops the surface committing, so the row has to say
  // why rather than leave the editor looking like it simply stopped working.
  const operatorError = fieldError(errors, 'operator');
  const valueError = valueTouched ? fieldError(errors, 'value') : undefined;

  const handleOperatorChange = (nextOperator: string | null) => {
    if (nextOperator == null || nextOperator === operator) {
      return;
    }
    const clearAll = clearsValues(nextOperator, operator);
    // Switching operators must not inherit touched state; a carried non-blank
    // value keeps showing its problems while a cleared one starts clean.
    setValueTouched(!clearAll && !isEmptyString(value));
    onChange({
      operator: nextOperator,
      value: clearAll ? null : value,
      value2: null,
    });
  };

  const showValue = !isUnaryFilterOperator(operator) && !isTwoValueFilterOperator(operator);

  return (
    <InlineStack alignBlock="baseline" gap="050" wrap>
      <Select
        data-testid="dryad-filter-operator-select"
        ariaLabel="Operator"
        size="sm"
        options={operatorGroups}
        value={operator}
        error={operatorError}
        onChange={handleOperatorChange}
        isDisabled={disabled}
      />
      {showValue ? (
        <Combobox
          data-testid="dryad-filter-value-input"
          ariaLabel="Value"
          className="fs-mask"
          size="sm"
          placeholder="Value"
          inputValue={value ?? ''}
          onValueChange={(nextValue) => onChange({ value: nextValue })}
          onBlur={() => setValueTouched(true)}
          options={valueOptions}
          optionComponent={MaskedValueOption}
          isLoading={isLoadingValueOptions}
          error={valueError}
          isDisabled={disabled}
          withVirtualizer
        />
      ) : null}
    </InlineStack>
  );
}
